import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _uniqBy from 'lodash/uniqBy';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import useContentfulLocale from 'components/useContentfulLocale';
import ctf from 'utils/contentfulService';
import { isReferenceAssessmentAccepted } from 'containers/Main/utils';

const SELECT_FIELDS =
  'sys,fields.type,fields.internalName,fields.commonPages,fields.dismissOptions,fields.pagePosition,fields.visibilityDefault,fields.header,fields.bodyContent,fields.backgroundColorCode,fields.backgroundColor,fields.sortOrder,fields.startDatetime,fields.endDatetime,fields.configurations,fields.associatedPages,fields.visibilityExceptions,fields.reference';

const mapAndFilterBlocks = ({ items, clientDetails }) => {
  const mapped = items
    .map(item => ({
      sys: item.sys,
      ...item.fields,
    }))
    .sort(
      (a, b) =>
        _get(a, 'sortOrder', Number.MAX_SAFE_INTEGER) -
        _get(b, 'sortOrder', Number.MAX_SAFE_INTEGER),
    )
    .filter(item => {
      if (item.startDatetime && item.endDatetime)
        return (
          Date.now() > new Date(`${item.startDatetime}T00:00:00`).getTime() &&
          Date.now() < new Date(`${item.endDatetime}T23:59:59`).getTime()
        );
      if (item.startDatetime)
        return (
          Date.now() > new Date(`${item.startDatetime}T00:00:00`).getTime()
        );
      if (item.endDatetime)
        return Date.now() < new Date(`${item.endDatetime}T23:59:59`).getTime();
      return true;
    })
    .filter(item =>
      isReferenceAssessmentAccepted({
        reference: _get(item, 'reference'),
        clientDetails,
      }),
    );

  return mapped;
};

const fetchContentBlocks = async (
  _key,
  { clientId, clientGroupId, localeFilters },
) => {
  const commonParams = {
    content_type: 'contentBlock',
    select: SELECT_FIELDS,
    'fields.reviewStatus': 'Accepted',
    order: '-sys.updatedAt',
    limit: 200,
    ...localeFilters,
  };

  const ids = [clientId, clientGroupId].filter(Boolean).join(',');

  if (!ids) {
    // No brand context: return only Show for All with no exceptions
    const onlyShowForAllNoExceptions = await ctf.getEntries({
      ...commonParams,
      'fields.visibilityDefault': visibilityDefaults.showForAll,
      'fields.visibilityExceptions[exists]': false,
    });
    const items = _get(onlyShowForAllNoExceptions, 'items', []);
    return items;
  }

  const [hideDefaultForBrand, showForAllAllowed] = await Promise.all([
    ctf.getEntries({
      ...commonParams,
      'fields.visibilityDefault[ne]': visibilityDefaults.showForAll,
      'fields.visibilityExceptions.sys.id[in]': ids,
    }),
    ctf.getEntries({
      ...commonParams,
      'fields.visibilityDefault': visibilityDefaults.showForAll,
      'fields.visibilityExceptions.sys.id[nin]': ids,
    }),
  ]);

  const merged = [
    ..._get(hideDefaultForBrand, 'items', []),
    ..._get(showForAllAllowed, 'items', []),
  ];
  const unique = _uniqBy(merged, entry => _get(entry, 'sys.id'));
  return unique;
};

const visibilityDefaults = {
  showForAll: 'Show for All',
};

const useContentBlocks = ({ queryOptions = {} } = {}) => {
  const clientDetails = useSelector(makeSelectClientDetails());
  const localeFilters = useContentfulLocale();

  const clientId = _get(clientDetails, 'sys.id');
  const clientGroupId = _get(clientDetails, 'clientGroup.sys.id');

  const result = useQuery(
    ['contentBlocks', { clientId, clientGroupId, localeFilters }],
    fetchContentBlocks,
    {
      staleTime: 60 * 60 * 1000,
      ...queryOptions,
    },
  );

  const finalBlocks = useMemo(
    () => mapAndFilterBlocks({ items: result.data || [], clientDetails }),
    [result.data, clientDetails],
  );

  return {
    ...result,
    data: finalBlocks,
  };
};

export default useContentBlocks;
